import type { EmailStatusChip } from "~/helpers/emailSendFormat";

import { type EmailDeliveryStatus, type EmailSendSource } from "~/gql/graphqlBase.generated";
import { emailDeliveryStatusChip } from "~/helpers/emailSendFormat";

export const RUN_HISTORY_GRID_ID = "runHistory";

export const RunHistoryKind = {
	EmailSend: "EMAIL_SEND",
	Execution: "EXECUTION"
} as const;
export type RunHistoryKind = (typeof RunHistoryKind)[keyof typeof RunHistoryKind];

export const RunHistoryStatus = {
	Failed: "FAILED",
	SendFailed: "SEND_FAILED",
	Sent: "SENT",
	Success: "SUCCESS"
} as const;
export type RunHistoryStatus = (typeof RunHistoryStatus)[keyof typeof RunHistoryStatus];

export interface RunHistoryRow {
	bounceClassification?: string | null;
	deliveryStatus?: EmailDeliveryStatus | null;
	errorMessage?: string | null;
	fromEmail?: string | null;
	kind: string;
	occurredAt: string;
	orderId?: string | null;
	orderNumber?: string | null;
	pdfSizeBytes?: number | null;
	recipientCount: number;
	recipientSummary?: string | null;
	reportId?: string | null;
	reportName?: string | null;
	rowId: string;
	scheduleId?: string | null;
	source: EmailSendSource;
	status: string;
	subject?: string | null;
	success: boolean;
	triggeredBy: string;
}

type ChipColor = "default" | "error" | "info" | "success" | "warning";

export interface RunHistoryStatusChip {
	color: ChipColor;
	label: string;
}

const STATUS_CHIPS: Record<RunHistoryStatus, RunHistoryStatusChip> = {
	FAILED: { color: "error", label: "Failed" },
	SEND_FAILED: { color: "error", label: "Send Failed" },
	SENT: { color: "success", label: "Sent" },
	SUCCESS: { color: "success", label: "Success" }
};

export const runHistoryReferenceLabel = (
	row: Pick<RunHistoryRow, "orderId" | "orderNumber" | "reportName">
): string => {
	if (row.reportName) return row.reportName;
	if (row.orderNumber) return `Order #${row.orderNumber}`;
	if (row.orderId) return row.orderId;
	return "—";
};

export const runHistoryRecipientEmails = (recipientSummary?: string | null): string[] =>
	recipientSummary
		? recipientSummary
				.split(",")
				.map((email) => email.trim())
				.filter(Boolean)
		: [];

export const runHistoryStatusChip = (status: string): RunHistoryStatusChip =>
	STATUS_CHIPS[status as RunHistoryStatus] ?? { color: "default", label: status || "—" };

export const runHistoryDeliveryChip = (deliveryStatus?: EmailDeliveryStatus | null): EmailStatusChip | null =>
	deliveryStatus ? emailDeliveryStatusChip(deliveryStatus) : null;

export const runHistoryHasDeliveryDetail = (row: RunHistoryRow): boolean =>
	Boolean(row.deliveryStatus || row.fromEmail || row.bounceClassification || row.recipientSummary);
